import { useEffect, useState } from "react";
import { Megaphone, Trash2, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { formatDateTime } from "@/constants";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SOURCES = [
  { value: "cetf", label: "CETF (nous)" },
  { value: "toury", label: "PanneauPocket · Toury" },
  { value: "janville", label: "PanneauPocket · Janville" },
  { value: "beauce", label: "PanneauPocket · CC Cœur de Beauce" },
];
const SOURCE_BADGE = {
  cetf: "bg-emerald-100 text-emerald-800 border-emerald-200",
  toury: "bg-sky-100 text-sky-800 border-sky-200",
  janville: "bg-amber-100 text-amber-800 border-amber-200",
  beauce: "bg-violet-100 text-violet-800 border-violet-200",
};
const EMPTY = { text: "", source: "cetf", link: "", media_url: "", active: true };

export default function AdminTicker() {
  const [items, setItems] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const [feedForm, setFeedForm] = useState({ label: "", url: "" });
  const [feedBusy, setFeedBusy] = useState(false);
  const [famNews, setFamNews] = useState(null);
  const [famBusy, setFamBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/ticker").then((r) => setItems(r.data)).catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  const loadFeeds = () => api.get("/admin/ticker-feeds").then((r) => setFeeds(r.data)).catch((e) => { toast.error(formatApiError(e)); setFeeds([]); });
  useEffect(() => { load(); loadFeeds(); loadFam(); }, []);

  const loadFam = () => api.get("/admin/family-news").then((r) => setFamNews(r.data)).catch((e) => { toast.error(formatApiError(e)); setFamNews([]); });

  const moderateFam = async (it, status) => {
    try {
      await api.patch(`/admin/family-news/${it.id}`, { status });
      loadFam();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeFam = async (it) => {
    try {
      await api.delete(`/admin/family-news/${it.id}`);
      toast.success("Actualité retirée du fil.");
      loadFam();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const refreshFam = async () => {
    setFamBusy(true);
    try {
      const r = await api.post("/admin/family-news/refresh");
      toast.success(`${r.data.count} actualité(s) relevée(s).`);
      loadFam();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFamBusy(false);
    }
  };

  const createFeed = async (e) => {
    e.preventDefault();
    setFeedBusy(true);
    try {
      const r = await api.post("/admin/ticker-feeds", feedForm);
      toast.success(r.data.kind === "rss" ? "Flux RSS détecté et ajouté." : "Site web ajouté — ses titres seront repris automatiquement.");
      setFeedForm({ label: "", url: "" });
      loadFeeds();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFeedBusy(false);
    }
  };

  const toggleFeed = async (f) => {
    try {
      await api.patch(`/admin/ticker-feeds/${f.id}`, { active: !f.active });
      loadFeeds();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeFeed = async (f) => {
    try {
      await api.delete(`/admin/ticker-feeds/${f.id}`);
      toast.success("Source supprimée.");
      loadFeeds();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = new FormData();
      data.append("text", form.text);
      data.append("source", form.source);
      data.append("link", form.link);
      data.append("media_url", form.media_url);
      data.append("active", form.active);
      if (file) data.append("file", file);
      await api.post("/admin/ticker", data);
      toast.success("Actualité ajoutée au fil.");
      setForm(EMPTY);
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/admin/ticker/${it.id}`, { active: !it.active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/admin/ticker/${it.id}`);
      toast.success("Actualité supprimée.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-ticker-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-800 flex items-center justify-center shrink-0">
          <Megaphone className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-ticker-title">Fil d'actualité</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Bandeau défilant de l'accueil — vos annonces + les panneaux Pocket de Toury, Janville et la CC Cœur de Beauce (relevé automatique toutes les 30 min).
          </p>
        </div>
      </div>

      <Card className="border-slate-200" data-testid="ticker-form-card">
        <CardContent className="p-6">
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ticker-text">Texte de l'annonce *</Label>
              <Input id="ticker-text" required maxLength={280} data-testid="ticker-text-input"
                placeholder="Ex. : Forum des associations samedi — venez nous rencontrer !"
                value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
              <p className="text-xs text-slate-400">{280 - form.text.length} caractères restants</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Source affichée</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger data-testid="ticker-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`ticker-source-option-${s.value}`}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ticker-link">Lien (facultatif)</Label>
                <Input id="ticker-link" type="url" maxLength={300} placeholder="https://…" data-testid="ticker-link-input"
                  value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticker-media">Image ou vidéo (facultatif, 10 Mo max)</Label>
              <label htmlFor="ticker-media" className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-3.5 cursor-pointer hover:bg-slate-50 transition-colors" data-testid="ticker-media-drop">
                <Upload className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="text-sm text-slate-600">{file ? file.name : "Joindre une image ou une vidéo MP4"}</span>
              </label>
              <input id="ticker-media" type="file" accept="image/*,video/mp4,video/quicktime" className="hidden" data-testid="ticker-media-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticker-media-url">…ou adresse d'une image en ligne (facultatif)</Label>
              <Input id="ticker-media-url" type="url" maxLength={500} placeholder="https://…/image.jpg" data-testid="ticker-media-url-input"
                value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })} />
            </div>
            <div className="flex items-center gap-2.5">
              <Checkbox id="ticker-active" data-testid="ticker-active-checkbox"
                checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v === true })} />
              <Label htmlFor="ticker-active" className="text-sm font-normal text-slate-600 cursor-pointer">Visible immédiatement dans le bandeau</Label>
            </div>
            <Button type="submit" className="bg-slate-800 hover:bg-slate-900" disabled={busy || !form.text.trim()} data-testid="ticker-create-btn">
              {busy ? "Ajout…" : "Ajouter au fil"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <section data-testid="ticker-feeds-section">
        <h2 className="text-lg font-bold text-slate-800 mb-3">Sources externes automatiques</h2>
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-slate-500 leading-relaxed">
              Ajoutez l'adresse d'un <strong>flux RSS</strong> (…/rss, .xml…) ou d'un <strong>site web</strong> :
              le fil détecte le format tout seul et reprend automatiquement les derniers titres (relevé toutes les 30 min),
              en plus des panneaux Pocket déjà branchés.
            </p>
            <form onSubmit={createFeed} className="grid grid-cols-1 sm:grid-cols-[220px_1fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label htmlFor="feed-label">Nom affiché *</Label>
                <Input id="feed-label" required maxLength={120} placeholder="Ex. : Mairie de Toury" data-testid="feed-label-input"
                  value={feedForm.label} onChange={(e) => setFeedForm({ ...feedForm, label: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feed-url">Adresse du flux RSS ou du site *</Label>
                <Input id="feed-url" required type="url" maxLength={500} placeholder="https://…" data-testid="feed-url-input"
                  value={feedForm.url} onChange={(e) => setFeedForm({ ...feedForm, url: e.target.value })} />
              </div>
              <Button type="submit" className="bg-slate-800 hover:bg-slate-900" disabled={feedBusy} data-testid="feed-create-btn">
                {feedBusy ? "Vérification…" : "Ajouter la source"}
              </Button>
            </form>
            {feeds === null ? (
              <p className="text-sm text-slate-400">Chargement…</p>
            ) : feeds.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="feeds-empty">Aucune source externe personnalisée pour le moment.</p>
            ) : (
              <div className="space-y-2.5">
                {feeds.map((f) => (
                  <div key={f.id} className={`flex flex-wrap items-center gap-x-3 gap-y-2 border border-slate-200 rounded-xl p-3.5 ${f.active ? "" : "opacity-60"}`}
                    data-testid={`feed-item-${f.id}`}>
                    <Badge className={`border ${f.kind === "rss" ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}
                      data-testid={`feed-kind-${f.id}`}>
                      {f.kind === "rss" ? "Flux RSS" : "Site web"}
                    </Badge>
                    <div className="min-w-[200px] flex-1">
                      <p className="text-sm font-medium text-slate-800">{f.label}</p>
                      <p className="text-xs text-slate-400 truncate">{f.url}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => toggleFeed(f)} data-testid={`feed-toggle-${f.id}`}>
                      {f.active ? "Désactiver" : "Activer"}
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => removeFeed(f)} data-testid={`feed-delete-${f.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <section data-testid="famnews-section">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <h2 className="text-lg font-bold text-slate-800">Actualités des familles — sources institutionnelles</h2>
          <Button variant="outline" size="sm" className="ml-auto" onClick={refreshFam} disabled={famBusy} data-testid="famnews-refresh-btn">
            {famBusy ? "Actualisation…" : "Actualiser maintenant"}
          </Button>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Relevé automatique toutes les six heures (Service-Public, 3018, Je protège mon enfant…).
          Masquez temporairement ou retirez définitivement ce qui ne convient pas.
        </p>
        {famNews === null ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : famNews.length === 0 ? (
          <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-5" data-testid="famnews-empty">
            Aucune actualité relevée pour le moment.
          </p>
        ) : (
          <div className="space-y-2.5">
            {famNews.map((it) => (
              <div key={it.id} className={`bg-white border border-slate-200 rounded-xl p-3.5 flex flex-wrap items-center gap-x-3 gap-y-2 ${it.moderation === "hidden" ? "opacity-55" : ""}`}
                data-testid={`famnews-item-${it.id}`}>
                <Badge className={`border ${SOURCE_BADGE[it.source] || "bg-slate-50 text-slate-700 border-slate-200"}`}>{it.source_label}</Badge>
                <div className="min-w-[220px] flex-1">
                  <p className="text-sm font-medium text-slate-800 leading-snug">{it.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{it.date ? formatDateTime(it.date) : "date indisponible"}{it.moderation === "hidden" ? " · masquée du site" : ""}</p>
                </div>
                {it.moderation === "hidden" ? (
                  <Button variant="outline" size="sm" onClick={() => moderateFam(it, "active")} data-testid={`famnews-show-${it.id}`}>Réafficher</Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => moderateFam(it, "hidden")} data-testid={`famnews-hide-${it.id}`}>Masquer</Button>
                )}
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => removeFam(it)} data-testid={`famnews-delete-${it.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="ticker-list-section">
        <h2 className="text-lg font-bold text-slate-800 mb-3">Annonces du fil ({(items || []).length})</h2>
        {items === null ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-5" data-testid="ticker-empty">
            Aucune annonce manuelle — le bandeau affiche alors uniquement les relevés Panneau Pocket (s'ils sont disponibles).
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <Card key={it.id} className={`border-slate-200 ${it.active ? "" : "opacity-60"}`} data-testid={`ticker-item-${it.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {it.media_url && it.media_type === "image" && (
                    <img src={`${API_BASE}${it.media_url}`} alt="" className="h-11 w-11 rounded-lg object-cover border border-slate-200" />
                  )}
                  {it.media_url && it.media_type === "video" && (
                    <video src={`${API_BASE}${it.media_url}`} muted loop playsInline autoPlay className="h-11 w-16 rounded-lg object-cover border border-slate-200" data-testid={`ticker-video-${it.id}`} />
                  )}
                  <div className="min-w-[220px] flex-1">
                    <p className="text-sm font-medium text-slate-800">{it.text}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Ajoutée le {formatDateTime(it.created_at)}{it.link ? ` · ${it.link}` : ""}</p>
                  </div>
                  <Badge className={`border hover:bg-opacity-100 ${SOURCE_BADGE[it.source] || SOURCE_BADGE.cetf}`}>{it.source_label}</Badge>
                  <Button variant="outline" size="sm" onClick={() => toggle(it)} data-testid={`ticker-toggle-${it.id}`}>
                    {it.active ? "Masquer" : "Afficher"}
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(it)} data-testid={`ticker-delete-${it.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
